import React from 'react';
import {
  Chip,
  Stack,
  Button,
  Link as MuiLink,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
  Card,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountStats } from '../../../hooks/api/useAccountStats';
import { getAccountStatus, isAccountExpired } from '../utils';
import dayjs from 'dayjs';
import { AccountStatusBadge } from './AccountStatusBadge';

interface AccountsTableMobileProps {
  accounts: AccountStats[];
  loggedInAccount: string;
  searchTerm: string;
  expandedExtensions: Set<string>;
  onToggleExtension: (key: string) => void;
  loadingRows: Set<string>;
  onDisableClick: (accountId: string, event: React.MouseEvent) => void;
  onEdit: (account: AccountStats, event: React.MouseEvent) => void;
  onAgreementClick: (accountId: string, event: React.MouseEvent) => void;
  onBrandingClick: (accountId: string, event: React.MouseEvent) => void;
  searchUrl: string;
}

export const AccountsTableMobile: React.FC<AccountsTableMobileProps> = ({
  accounts,
  loggedInAccount,
  searchTerm,
  expandedExtensions,
  onToggleExtension,
  loadingRows,
  onDisableClick,
  onEdit,
  onAgreementClick,
  onBrandingClick,
  searchUrl,
}) => {
  const { t } = useTranslation();

  if (accounts.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1">
          {searchTerm
            ? t('accounts.noAccountsFoundWithSearch', {
                searchTerm,
              })
            : t('accounts.noAccountsFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={12}>
      {accounts.map((account) => {
        const isExpired = isAccountExpired(account.contract_expiration_date);
        const isLoading = loadingRows.has(account._id);
        return (
          <Card
            variant="primary"
            key={account._id}
            sx={{
              opacity: isLoading ? 0.5 : isExpired ? 0.6 : 1,
              '&:hover': { cursor: 'default' },
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption">
                  {t('accounts.accountId')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant={
                      account.agreements?.active ? 'link' : 'warningLink'
                    }
                    endIcon={<OpenInNew fontSize="small" />}
                    href={`${searchUrl}/${account._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {account._id}
                  </Button>
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">{t('accounts.name')}</Typography>
                {account.name ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Tooltip title={t('accounts.goToBranding')}>
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={(e) => onBrandingClick(account._id, e)}
                        sx={{
                          display: 'inline-flex',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 0,
                          padding: 0,
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                      >
                        {account.name}
                      </MuiLink>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    -
                  </Typography>
                )}
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.status')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <AccountStatusBadge status={getAccountStatus(account)} />
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.active')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {account.agreements?.active ? (
                    <Chip
                      label={`${account.agreements.active.agreements}/${account.agreements.active.journals}`}
                      size="small"
                      variant="success"
                      onClick={(e) => onAgreementClick(account._id, e)}
                    />
                  ) : (
                    <Typography variant="body1">-</Typography>
                  )}
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.expired')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {account.agreements?.expired ? (
                    <Chip
                      label={`${account.agreements.expired.agreements}/${account.agreements.expired.journals}`}
                      size="small"
                      variant="warning"
                      onClick={(e) => onAgreementClick(account._id, e)}
                    />
                  ) : (
                    <Typography variant="body1">-</Typography>
                  )}
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.contractExpiration')}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {account.contract_expiration_date
                    ? dayjs(account.contract_expiration_date).format(
                        'YYYY-MM-DD'
                      )
                    : '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.lastLogin')}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {account.last_login
                    ? dayjs(account.last_login).format('YYYY-MM-DD')
                    : '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('accounts.extensions')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {account.extensions &&
                  Object.keys(account.extensions).length > 0 ? (
                    <Stack spacing={0.5}>
                      {Object.entries(account.extensions).map(
                        ([key, values]) => {
                          const extensionKey = `${account._id}-${key}`;
                          const isExpanded =
                            expandedExtensions.has(extensionKey);
                          return (
                            <Chip
                              key={key}
                              size="small"
                              variant="default"
                              clickable
                              onClick={() => onToggleExtension(extensionKey)}
                              label={
                                !Array.isArray(values)
                                  ? `${key}: ${values}`
                                  : isExpanded
                                    ? `${key}: ${values.length} (${values.join(', ')})`
                                    : `${key}: ${values.length}`
                              }
                              sx={{
                                width: 'fit-content',
                              }}
                            />
                          );
                        }
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Box>
              </Box>
              {loggedInAccount === 'admin' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('accounts.actions')}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={12}
                      sx={{ mt: 0.5, justifyContent: 'end' }}
                    >
                      <Button
                        size="small"
                        variant="default"
                        startIcon={<EditIcon />}
                        onClick={(e) => onEdit(account, e)}
                        disabled={isLoading}
                      >
                        {t('accounts.edit')}
                      </Button>
                      <Button
                        size="small"
                        variant="warningSecondary"
                        startIcon={
                          isLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <BlockIcon />
                          )
                        }
                        onClick={(e) => onDisableClick(account._id, e)}
                        disabled={isLoading}
                      >
                        {t('accounts.disable')}
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
};
